import { IPickupDetails, Question } from "@common-types";

export interface IAgeRange {
  minAge: number;
  /** When omitted, no upper bound (any value >= minAge is valid). */
  maxAge?: number;
}

/** Per-band age limits from activity detail; when present for a guest `type`, checkout uses these instead of hardcoded ranges. */
export interface IActivityAgeBand {
  ageBand: string;
  startAge: number;
  endAge: number;
}

export function getActivityAgeBandBounds(
  type: string | null | undefined,
  ageBands?: IActivityAgeBand[] | null
): { startAge: number; endAge: number } | undefined {
  if (!type || !ageBands?.length) return undefined;
  const t = String(type).toUpperCase();
  const row = ageBands.find((b) => String(b.ageBand || '').toUpperCase() === t);
  if (!row) return undefined;
  const startAge = Number(row.startAge);
  const endAge = Number(row.endAge);
  if (!Number.isFinite(startAge) || !Number.isFinite(endAge)) return undefined;
  return { startAge, endAge };
}

/**
 * Picks the `ageBand` code for a numeric age using provider `ageBands` (first matching row by list order).
 * If `ageBands` is empty or no row contains `age`, returns `fallback` (e.g. `ADULT` / `CHILD` from the room list).
 */
export function getGuestTypeForAge(
  age: number | string | null | undefined,
  ageBands: IActivityAgeBand[] | null | undefined,
  fallback: string
): string {
  const n = age === '' || age === null || age === undefined ? NaN : Number(age);
  if (!Number.isFinite(n)) {
    return fallback;
  }
  if (!ageBands?.length) {
    return fallback;
  }
  for (const b of ageBands) {
    const s = Number(b.startAge);
    const e = Number(b.endAge);
    const code = String(b.ageBand || '').toUpperCase();
    if (!code || !Number.isFinite(s) || !Number.isFinite(e)) {
      continue;
    }
    if (n >= s && n <= e) {
      return code;
    }
  }
  return fallback;
}

/** Bounds for the activity-details guest/room picker when `ageBands` is present. */
export type RoomPickerAgeConfig = {
  minAdultAge: number;
  maxAdultAge: number | undefined;
  childMinAge: number;
  childMaxAge: number;
  showChildren: boolean;
  /** True when bounds came from a valid `ageBands` list with an `ADULT` row. */
  fromAgeBands: boolean;
};

/**
 * Derives room-picker adult/child age bounds. When `ageBands` is present and includes an `ADULT` row,
 * that band defines the adult minimum; the maximum is the largest `endAge` in the list. Child ages
 * cover all non-`ADULT` bands with `endAge` strictly below the `ADULT` `startAge` (min of their
 * `startAge`, max of their `endAge`). When `ageBands` is absent or no valid `ADULT` row, uses
 * `ageRange` and the previous defaults (adults 18+ unless `ageRange` raises min; children 0–17, etc.).
 */
export function getRoomPickerAgeConfig(
  ageRange: IAgeRange | undefined,
  ageBands: IActivityAgeBand[] | null | undefined
): RoomPickerAgeConfig {
  if (ageBands?.length) {
    const rows = ageBands
      .map((b) => ({
        name: String(b.ageBand || '').toUpperCase(),
        start: Number(b.startAge),
        end: Number(b.endAge),
      }))
      .filter((b) => Number.isFinite(b.start) && Number.isFinite(b.end));
    const adult = rows.find((b) => b.name === 'ADULT');
    if (adult) {
      const listMaxEnd = Math.max(...rows.map((b) => b.end));
      const nonAdultMinors = rows.filter((b) => b.name !== 'ADULT' && b.end < adult.start);
      if (nonAdultMinors.length) {
        const childMin = Math.min(...nonAdultMinors.map((m) => m.start));
        const childMax = Math.max(...nonAdultMinors.map((m) => m.end));
        return {
          minAdultAge: adult.start,
          maxAdultAge: listMaxEnd,
          childMinAge: childMin,
          childMaxAge: childMax,
          showChildren: true,
          fromAgeBands: true,
        };
      }
      return {
        minAdultAge: adult.start,
        maxAdultAge: listMaxEnd,
        childMinAge: 0,
        childMaxAge: 0,
        showChildren: false,
        fromAgeBands: true,
      };
    }
  }

  const minAdultAge = Math.max(18, ageRange?.minAge ?? 18);
  const maxAdultAge = ageRange?.maxAge;
  const showChildren = !(ageRange != null && ageRange.minAge >= 18);
  const childMinAge = ageRange != null && ageRange.minAge < 18 ? ageRange.minAge : 0;
  const childMaxAge = 17;
  return {
    minAdultAge,
    maxAdultAge,
    childMinAge,
    childMaxAge,
    showChildren,
    fromAgeBands: false,
  };
}

export interface IActivityDetail {
  code: string;
  name: string;
  currency: string;
  modalities: Modality[];
  content: ActivityContent;
  reviews?: ActivityReviews;
  /** When present, restricts guest ages. When minAge >= 18, children are hidden; adults use this range for labels and validation. */
  ageRange?: IAgeRange;
  /** Optional provider-defined age band ranges (e.g. TRAVELER 0–120). Used at checkout when `ageBand` matches guest type or question option code. */
  ageBands?: IActivityAgeBand[];
  savedActivity?: boolean;
  /** Viator-only: bookable days (yyyy-MM-dd) inside the requested window, used to paint the calendar. */
  availableDates?: string[];
}



export interface Modality {
  code: string;
  name: string;
  durationValue: number;
  durationMetric: string;
  questions: Question[];
  comments: any[];
  rates: ActivityRate[];
  languageGuides?: LanguageGuide[];
}
export interface ActivitySession {
  code: string;
  name: string;
  available: boolean;
  /** HotelBeds: operation date (yyyy-MM-dd) this slot is bookable on. With code/name (time) it is a concrete bookable option. */
  date?: string;
}

/** A single tax entry on an activity rate. Card-charged taxes are those with `atProperty === false`. */
export interface IActivityTax {
  amount: number;
  /** When true the tax is collected at the property/venue, not on the card at booking. */
  atProperty?: boolean;
  /** When true the tax is optional (not always charged). */
  isOptional?: boolean;
  type?: string;
}

export interface ActivityRate {
  rateKey: string;
  providerRateKey: string;
  freeCancellation: boolean;
  refundable: boolean;
  price: number;
  /** Pre-tax base price (price − taxes). Basis for the wallet/points discount cap. */
  priceWithoutTaxes?: number;
  saving: number;
  savingPercent: number;
  publicPrices: PublicPrice[];
  maxWalletClientCash: number;
  breakdownTaxes: boolean;
  taxes?: IActivityTax[];
  sessions: ActivitySession[];
  cancellationPolicies: CancellationPolicy[];
  cancellationPolicyText?: string;
  /** HotelBeds: guide language (ISO, e.g. "en"/"es"); labels same-time slots that differ only by language. */
  guideLanguage?: string;
  /** Per-rate currency; falls back to the activity's when absent. */
  currency?: string;
}

export interface PublicPrice {
  supplierId: string;
  price: number;
}

export interface CancellationPolicy {
  from: string; // ISO Date
  to: string;   // ISO Date
  currency: string;
  supplierAmount: number;
  amount: number;
  default: boolean;
  after: boolean;
}

export type ItineraryItem = {
  order: number;
  name: string;
  description: string;
  formattedDuration: string;
};

export interface ActivityContent {
  mediaImages: MediaImage[];
  importantInfo: string[];
  endPoints: EndPoint[];
  startingPoints: StartingPoint[];
  travelerPickups: IPickupDetails[];
  allowCustomTravelerPickup?: boolean;
  redeemInfoDirectEntrance: boolean;
  redeemInfoComments: string[];
  description: string;
  summary: string;
  highlights: string[];
  inclusions?: string[];
  exclusions?: string[];
  activityCode?: string;
  tags?: string[];
  itineraryItems: ItineraryItem[];
  itineraryItems3?: ItineraryItem[];
}

export interface MediaImage {
  visualizationOrder: number;
  mimeType: string;
  language: string;
  dpi: number;
  height: number;
  width: number;
  resource: string;
  sizeType: 'COVER' | 'REGULAR';
}

export interface LanguageGuide {
  type: string;
  language: string;
}

export interface ActivityReviews {
  total: number;
  averageRating: number;
}

export type ImageSizeType = 'XLARGE' | 'LARGE' | 'MEDIUM' | 'SMALL';


export interface ILocationPoint {
  latitude?: number;
  longitude?: number;
  address?: string;
  countryCode?: string;
  countryName?: string;
  destinationCode?: string;
  destinationName?: string;
  city?: string;
  zip?: string;
  description: string;
  pickupInstructionsDescriptions?: string[];
  reference?: string;
}

export interface EndPoint extends ILocationPoint {
  type: 'Same' | 'Different';
}

export interface StartingPoint extends ILocationPoint {
  type: 'Meeting Point' | 'PICKUP' | 'START';
  meetingPointType?: 'ADDRESS' | 'COORDINATES' | 'OTHER';
}



export type RedeemInfoType = 'VOCUHERLESS' | 'VOUCHER';
